import { useState, useEffect, useContext } from 'react'
import { Link, useLocation } from 'react-router-dom'
import MenuIcon from '@material-ui/icons/Menu'
import CloseIcon from '@material-ui/icons/Close'
import { ThemeContext } from '../../contexts/theme'
import { apkDownloadUrl } from '../../portfolio' 

const Navbar = () => {
  const [showNavList, setShowNavList] = useState(false)
  const [isScrolled, setIsScrolled] = useState(false)
  const [isStandalone, setIsStandalone] = useState(false) 
  const location = useLocation()
  const [{ themeName, toggleTheme }] = useContext(ThemeContext)

  const toggleNavList = () => setShowNavList(!showNavList)

  useEffect(() => {
    // 👈 Check if running as an installed app
    const checkStandalone = 
      window.matchMedia('(display-mode: standalone)').matches || 
      window.navigator.standalone;
    setIsStandalone(checkStandalone);

    const handleScroll = () => {
      setIsScrolled(window.scrollY > 40)
    }
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const handleNavClick = (href) => {
    setShowNavList(false)
    if (location.pathname !== '/' && href.startsWith('/#')) {
      const sectionId = href.replace('/#', '')
      window.location.href = `/#${sectionId}`
    }
  }

  const navItems = [
    { name: 'Home', href: '/' },
    { name: 'About', href: '/about' },
    // { name: 'Projects', href: '/projects' },
    { name: 'Contact', href: '/contact' }
  ]

  const isActive = (href) => {
    if (href === '/') return location.pathname === '/'
    return location.pathname.startsWith(href)
  }

  return (
    <nav className={` top-0 left-0 right-0 z-50 transition-all duration-700 ease-juicy ${
      isScrolled || showNavList
        ? 'py-2 md:py-0'
        : 'py-4 md:py-5'
    }`}>
      {/* Gradient border bottom reveal on scroll */}
      <div className={`absolute left-0 right-0 bottom-0 bg-gradient-brand transition-opacity duration-700 ${
        isScrolled ? 'opacity-100' : 'opacity-0'
      }`} />

      <div className={`max-w-7xl mx-auto px-3 sm:px-5 lg:px-7 transition-all duration-700 ${
        isScrolled || showNavList
          ? 'mt-0 md:mt-0'
          : ''
      }`}>
        <div className={`relative flex justify-between items-center  transition-all duration-700 ease-juicy overflow-hidden ${
          isScrolled || showNavList
            ? 'bg-white dark:bg-slate-950 shadow-md py-3 px-4 md:px-6'
            : 'bg-transparent py-2 px-2'
        }`}>
          {/* subtle shine overlay on scrolled */}
          {(isScrolled || showNavList) && (
            <div className="absolute inset-0 shine-overlay opacity-40 pointer-events-none" />
          )}

          <Link
            to='/'
            onClick={() => handleNavClick('/')}
            className='relative z-10 group flex items-center gap-2'
          >
            <span className='text-xl md:text-2xl font-extrabold font-space tracking-tight'>
              <span className="text-gradient-brand">Skill</span>
              <span className="text-gray-900 dark:text-white">Ex</span>
            </span>
          </Link>

          <div className='hidden md:flex items-center gap-3 lg:gap-5 relative z-10'>
            {navItems.map((item) => {
              const active = isActive(item.href)
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => handleNavClick(item.href)}
                  className={`relative px-5 lg:px-6 py-2.5 rounded-xl font-semibold transition-all duration-300 ease-juicy group ${
                    active
                      ? 'text-primary'
                      : 'text-gray-700 dark:text-slate-100 hover:text-primary'
                  }`}
                >
                  {/* Active / hover pill background */}
                  <span className={`absolute inset-0 rounded-xl transition-all duration-300 ${
                    active
                      ? 'bg-gradient-to-r from-primary/10 to-secondary/10 shadow-inner-glow'
                      : 'bg-transparent'
                  }`} />

                  <span className="relative z-10 flex items-center gap-1">
                    {item.name}
                    {active && (
                      <span className="relative flex h-1.5 w-1.5 ml-0.5">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-primary"></span>
                      </span>
                    )}
                  </span>

                  {/* animated underline */}
                  <span className={`absolute left-1/2 -translate-x-1/2 -bottom-0.5 h-0.5 rounded-full bg-gradient-brand transition-all duration-300 ease-juicy ${
                    active ? 'w-6 opacity-100' : 'w-0 group-hover:w-4 opacity-0 group-hover:opacity-100'
                  }`} />
                </Link>
              )
            })}

            <button
              type='button'
              onClick={toggleTheme}
              role='switch'
              aria-checked={themeName === 'dark'}
              aria-label='Dark mode'
              title={themeName === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
              className='ml-2 relative inline-flex h-7 w-12 flex-shrink-0 items-center rounded-full border border-slate-300 dark:border-white/40 bg-slate-300 dark:bg-primary shadow-inner transition-colors duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2'
            >
              <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform duration-300 ${themeName === 'dark' ? 'translate-x-6' : 'translate-x-1'}`} />
            </button>
          </div>

          <button
            type='button'
            onClick={toggleTheme}
            role='switch'
            aria-checked={themeName === 'dark'}
            aria-label='Dark mode'
            title={themeName === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
            className='md:hidden absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10 inline-flex h-7 w-12 flex-shrink-0 items-center rounded-full border border-slate-300 dark:border-white/40 bg-slate-300 dark:bg-primary shadow-inner transition-colors duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2'
          >
            <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform duration-300 ${themeName === 'dark' ? 'translate-x-6' : 'translate-x-1'}`} />
          </button>

          <button
            type='button'
            onClick={toggleNavList}
            className='md:hidden relative z-10 ml-auto w-11 h-11 flex items-center justify-center rounded-2xl bg-white/80 dark:bg-slate-900/80 backdrop-blur-md border border-primary/10 dark:border-white/50 text-gray-700 dark:text-white hover:text-primary dark:hover:text-white hover:bg-white dark:hover:bg-slate-800 hover:border-primary/30 hover:scale-105 active:scale-95 transition-all duration-300 shadow-soft'
            aria-label={showNavList ? 'Close navigation menu' : 'Open navigation menu'}
            aria-expanded={showNavList}
          >
            {showNavList ? <CloseIcon fontSize='small' /> : <MenuIcon fontSize='small' />}
          </button>
        </div>

        {/* Mobile menu */}
        <div
          className={`md:hidden overflow-hidden transition-all duration-500 ease-juicy ${
            showNavList ? 'max-h-[520px] opacity-100 mt-3' : 'max-h-0 opacity-0 mt-0'
          }`}
        >
          <div className='bg-white dark:bg-slate-900 p-4 shadow-xl border-t border-gray-200 dark:border-slate-800 relative overflow-hidden'>
            <div className="absolute inset-0 bg-gradient-mesh opacity-40 pointer-events-none" />

            <div className='relative z-10 flex flex-col gap-2.5'>
              {navItems.map((item, index) => {
                const active = isActive(item.href)
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    onClick={() => handleNavClick(item.href)}
                    className={`relative overflow-hidden font-semibold transition-all duration-400 py-3 px-5 flex items-center justify-between group ${
                      active
                        ? 'bg-gradient-to-r from-primary/15 via-secondary/20 to-primary/10 text-primary shadow-inner-glow border border-primary/20'
                        : 'text-gray-700 dark:text-slate-100 hover:bg-white/70 dark:hover:bg-white/10 hover:text-primary dark:hover:text-white border border-transparent'
                    }`}
                    style={{ animation: showNavList ? `fadeUp 0.5s ease-juicy ${index * 80}ms both` : 'none' }}
                  >
                    <span>{item.name}</span>
                    <span className={`text-xl transition-transform duration-300 ${
                      active ? 'translate-x-0 opacity-100' : 'opacity-0 -translate-x-2 group-hover:translate-x-0 group-hover:opacity-100'
                    }`}>→</span>

                    {active && (
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 flex h-2 w-2">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-primary"></span>
                      </span>
                    )}
                  </Link>
                )
              })}

              {/* NEW: App Download Link (Hidden inside the installed APK) */}
              {!isStandalone && (
                <a
                  href={apkDownloadUrl}
                  download="SkillEx.apk"
                  onClick={() => setShowNavList(false)}
                  className="mt-2 relative overflow-hidden font-semibold transition-all duration-400 py-3 px-5 flex items-center justify-center gap-2 bg-primary text-white shadow-juicy hover:-translate-y-0.5"
                  style={{ animation: showNavList ? `fadeUp 0.5s ease-juicy ${navItems.length * 80}ms both` : 'none' }}
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.523 15.341c-.507 0-.92.413-.92.92s.413.92.92.92.92-.413.92-.92-.413-.92-.92-.92zm-11.046 0c-.507 0-.92.413-.92.92s.413.92.92.92.92-.413.92-.92-.413-.92-.92-.92zm11.405-6.02l1.997-3.46a.416.416 0 00-.152-.57.416.416 0 00-.57.152l-2.022 3.502A12.02 12.02 0 0012 8.25c-1.86 0-3.61.423-5.135 1.175L4.843 5.923a.416.416 0 00-.152.57l1.997 3.46C2.9 11.25.75 14.355.75 17.91h22.5c0-3.555-2.15-6.66-5.368-8.589z" />
                  </svg>
                  Download App
                </a>
              )}
            </div>
          </div>
        </div>
      </div>
    </nav>
  )
}

export default Navbar